import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  IconButton
} from '@mui/material';
import { ArrowBack, Search } from '@mui/icons-material';
import AppBarContext from 'context/AppBarContext';
import routes from 'common/routes';
import { PLAY_EXERCISES } from 'api/graphql/play';
import PlayCatalogGroupNav from 'components/play/catalog/PlayCatalogGroupNav';
import PlayCatalogExerciseRow from 'components/play/catalog/PlayCatalogExerciseRow';
import PlayExerciseDetailModal from 'components/play/PlayExerciseDetailModal';
import AddToRoutineDialog from 'components/play/AddToRoutineDialog';
import { MUSCLE_GROUPS, LOCATIONS, muscleLabel, locationLabel } from 'components/play/constants';

/**
 * OTRIX Play — catálogo de ejercicios estilo BIBLIOTECA (dos columnas): grupos musculares
 * con contador a la izquierda, filas tipo pista a la derecha con hero del grupo activo y
 * buscador local. Lee ?muscle= de la URL (lo setea el mapa corporal) y solo renderiza el
 * grupo activo para no animar decenas de miniaturas a la vez.
 */
function PlayExercisesPage() {
  const { t } = useTranslation(['play']);
  const { setAppBarTitle } = useContext(AppBarContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const muscleParam = searchParams.get('muscle') || '';
  const [location, setLocation] = useState('');
  const [search, setSearch] = useState('');
  const [addTarget, setAddTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);

  useEffect(() => {
    setAppBarTitle(t('browser.title', { ns: 'play' }));
  }, [setAppBarTitle, t]);

  // Catálogo completo (solo filtro de ubicación): los contadores por grupo necesitan
  // el total, y el volumen (~130 ejercicios) es barato para el cliente.
  const { data, loading } = useQuery(PLAY_EXERCISES, {
    variables: { location: location || undefined },
    fetchPolicy: 'cache-and-network'
  });

  const exercises = useMemo(() => data?.playExercises || [], [data]);

  const byMuscle = useMemo(
    () =>
      exercises.reduce((acc, ex) => {
        const key = ex.muscle_group || 'other';
        (acc[key] = acc[key] || []).push(ex);
        return acc;
      }, {}),
    [exercises]
  );

  // Nav: "Todos" + grupos canónicos con ejercicios.
  const navGroups = useMemo(() => {
    const withItems = MUSCLE_GROUPS.filter((m) => (byMuscle[m.value] || []).length > 0).map((m) => ({
      value: m.value,
      count: (byMuscle[m.value] || []).length
    }));
    return [{ value: '', count: exercises.length }, ...withItems];
  }, [byMuscle, exercises.length]);

  // Secciones visibles: el grupo activo, o todas (en orden canónico) cuando "Todos".
  const sections = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = (ex) =>
      !q ||
      (ex.name || '').toLowerCase().includes(q) ||
      (ex.localized_name || '').toLowerCase().includes(q);
    const source = muscleParam
      ? MUSCLE_GROUPS.filter((m) => m.value === muscleParam)
      : MUSCLE_GROUPS;
    return source
      .map((m) => ({ value: m.value, items: (byMuscle[m.value] || []).filter(matches) }))
      .filter((s) => s.items.length > 0);
  }, [byMuscle, muscleParam, search]);

  const setMuscle = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('muscle', value);
    else next.delete('muscle');
    setSearchParams(next);
  };

  const heroTitle = muscleParam ? muscleLabel(t, muscleParam) : t('browser.all', { ns: 'play' });
  const heroCount = sections.reduce((sum, s) => sum + s.items.length, 0);
  const firstLoad = loading && exercises.length === 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* A este catálogo solo se llega desde "Ver todo el catálogo" de Mi rutina. */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton
          size="small"
          onClick={() => navigate(routes.playRoutine.path)}
          aria-label={t('browser.back', { ns: 'play', defaultValue: 'Volver' })}
          sx={{ border: '1px solid', borderColor: 'hairline.main' }}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography variant="body2" color="text.secondary" fontWeight={700}>
          {t('browser.backLabel', { ns: 'play', defaultValue: 'Mi rutina' })}
        </Typography>
      </Stack>

      <Grid container spacing={3}>
        {/* Columna izquierda: grupos */}
        <Grid item xs={12} md={3.5} lg={3}>
          {firstLoad ? (
            <Skeleton variant="rounded" height={320} sx={{ borderRadius: '16px', display: { xs: 'none', md: 'block' } }} />
          ) : (
            <PlayCatalogGroupNav groups={navGroups} active={muscleParam} onSelect={setMuscle} />
          )}
        </Grid>

        {/* Columna derecha: hero + filas */}
        <Grid item xs={12} md={8.5} lg={9}>
          <Stack spacing={2.5}>
            {/* Hero del grupo activo */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ sm: 'flex-end' }}
              justifyContent="space-between">
              <Stack spacing={0.25}>
                <Typography
                  variant="h4"
                  fontWeight={800}
                  letterSpacing="-0.02em"
                  sx={{ fontSize: { xs: '1.6rem', md: '2.125rem' } }}>
                  {heroTitle}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('browser.exerciseCount', { ns: 'play', count: heroCount, defaultValue: '{{count}} ejercicios' })}
                </Typography>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <TextField
                  size="small"
                  placeholder={t('browser.search', { ns: 'play', defaultValue: 'Buscar ejercicio' })}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{ minWidth: { sm: 210 } }}
                />
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={location}
                  onChange={(_, v) => setLocation(v || '')}
                  aria-label={t('browser.location', { ns: 'play' })}>
                  {LOCATIONS.map((l) => (
                    <ToggleButton key={l.value} value={l.value}>
                      {locationLabel(t, l.value)}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
            </Stack>

            {firstLoad && (
              <Stack spacing={1.5}>
                {[0, 1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} variant="rounded" height={104} sx={{ borderRadius: '14px' }} />
                ))}
              </Stack>
            )}

            {!firstLoad && sections.length === 0 && (
              <Box
                sx={{
                  py: 8,
                  textAlign: 'center',
                  border: '1px dashed',
                  borderColor: 'hairline.main',
                  borderRadius: 2,
                  color: 'text.secondary'
                }}>
                <Typography variant="body1" fontWeight={600}>
                  {t('browser.empty', { ns: 'play' })}
                </Typography>
                <Typography variant="body2">{t('browser.emptyHint', { ns: 'play' })}</Typography>
              </Box>
            )}

            {!firstLoad &&
              sections.map((section) => (
                <Stack key={section.value} spacing={1.5}>
                  {/* Subtítulo de sección solo en "Todos" (con grupo activo el hero ya lo dice). */}
                  {!muscleParam && (
                    <Typography
                      variant="overline"
                      sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em', lineHeight: 1 }}>
                      {muscleLabel(t, section.value)} · {section.items.length}
                    </Typography>
                  )}
                  {section.items.map((ex) => (
                    <PlayCatalogExerciseRow key={ex.id} exercise={ex} onAdd={setAddTarget} onOpenDetail={setDetailTarget} />
                  ))}
                </Stack>
              ))}
          </Stack>
        </Grid>
      </Grid>

      <PlayExerciseDetailModal exercise={detailTarget} open={Boolean(detailTarget)} onClose={() => setDetailTarget(null)} onAdd={setAddTarget} />
      <AddToRoutineDialog open={Boolean(addTarget)} exercise={addTarget} onClose={() => setAddTarget(null)} />
    </Box>
  );
}

export default PlayExercisesPage;
